import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { createClient } from '@liveblocks/client';
import { liveblocks } from '@liveblocks/zustand';
import { createRoomContext } from '@liveblocks/react';
import { create } from 'zustand';
import { CursorMode } from '../../types/presence.js';
import { liveblocksAuthEndpoint } from './fdocs.config.js';
import { useEffect } from 'react';
import { useSocketIOParentRoom } from '../../socket.io/ParentRoom/SocketIOParentRoomContext.js';
import { createLogger } from '@fabric-app/utils/logging';
import { useContextShareToken } from '../../permission/contexts/AnonShareTokenContext.js';

const client = createClient({
  authEndpoint: liveblocksAuthEndpoint
});
const useSpaceLiveblocksStore = create(liveblocks((set) => ({
  userId: "",
  setUserId: (userId) => {
    set({
      userId
    });
  },
  following: null,
  clickEvents: [],
  setClickEvents: (clickEvents) => {
    if (typeof clickEvents === "function") {
      set((state) => ({
        clickEvents: clickEvents(state.clickEvents)
      }));
      return;
    }
    set({
      clickEvents
    });
  },
  broadcastClickEvent: void 0,
  setBroadcastClickEvent: (broadcastClickEvent) => {
    set({
      broadcastClickEvent
    });
  },
  normalizedScrollTop: 0,
  normalizedScrollLeft: 0,
  setNormalizedScrollTop: (normalizedScrollTop) => {
    if (typeof normalizedScrollTop === "function") {
      set((state) => ({
        normalizedScrollTop: normalizedScrollTop(state.normalizedScrollTop)
      }));
      return;
    }
    set({
      normalizedScrollTop
    });
  },
  setNormalizedScrollLeft: (normalizedScrollLeft) => {
    if (typeof normalizedScrollLeft === "function") {
      set((state) => ({
        normalizedScrollLeft: normalizedScrollLeft(state.normalizedScrollLeft)
      }));
      return;
    }
    set({
      normalizedScrollLeft
    });
  },
  cursor: {
    state: {
      mode: CursorMode.Normal,
      type: "default"
    },
    lastWiggle: 0,
    position: {
      x: 0,
      y: 0,
      pressure: null
    }
  },
  setCursor: (cursor) => {
    if (typeof cursor === "function") {
      set((state) => ({
        cursor: cursor(state.cursor)
      }));
      return;
    }
    set({
      cursor
    });
  }
}), {
  client,
  presenceMapping: {
    cursor: true,
    normalizedScrollTop: true,
    normalizedScrollLeft: true,
    following: true
  }
}));
const folderRoomContext = createRoomContext(client);
const logger = createLogger("Liveblocks/Folder", true);
const { useOthers: useSpaceOthers, useSelf: useSpaceSelf, useUpdateMyPresence: useUpdateSpacePresence, useBroadcastEvent: useBroadcastSpaceEvent, useEventListener: useSpaceEventListener } = folderRoomContext;
const SpaceFolderRoomProvider = ({ children, resourceId, type = "space" }) => {
  const accessToken = useContextShareToken();
  const roomId = `${type}-${resourceId}`;
  const parentRoomFabricSocket = useSocketIOParentRoom();
  const isWrappedWithFabricSocketContext = typeof parentRoomFabricSocket !== "undefined";
  useEffect(() => {
    if (!isWrappedWithFabricSocketContext) {
      console.warn("ResourceRoomProvider: parentRoomFabricSocket is not defined. Wrap this component with SocketIOParentRoomProvider");
      return;
    }
  }, [
    isWrappedWithFabricSocketContext
  ]);
  const shouldConnect = Boolean(parentRoomFabricSocket && parentRoomFabricSocket?.connectedClients > 1) && accessToken?.type !== "publishing";
  if (!resourceId) {
    return /* @__PURE__ */ jsx(Fragment, {
      children
    });
  }
  return /* @__PURE__ */ jsxs(folderRoomContext.RoomProvider, {
    initialPresence: {
      following: null,
      playingResourceId: null,
      mediaTimestamp: 0,
      pdfPage: 1,
      cursor: {
        state: {
          mode: CursorMode.Normal,
          type: "default"
        },
        lastWiggle: 0,
        position: {
          x: 0,
          y: 0,
          pressure: null
        }
      },
      expandedResourceId: null,
      normalizedScrollTop: 0,
      normalizedScrollLeft: 0
    },
    autoConnect: shouldConnect,
    id: roomId,
    children: [
      /* @__PURE__ */ jsx(AutoConnector, {
        shouldConnect
      }),
      children
    ]
  }, roomId);
};
function withSpacerRoomProvider(Component, type) {
  const WithSpaceRoomProvider = (props) => props.resourceId ? /* @__PURE__ */ jsx(SpaceFolderRoomProvider, {
    resourceId: props.resourceId,
    type,
    children: /* @__PURE__ */ jsx(Component, {
      ...props
    })
  }) : /* @__PURE__ */ jsx(Component, {
    ...props
  });
  return WithSpaceRoomProvider;
}
const AutoConnector = ({ shouldConnect }) => {
  const room = folderRoomContext.useRoom();
  const status = folderRoomContext.useStatus();
  useEffect(() => {
    if (shouldConnect) {
      room.connect();
    } else {
      room.disconnect();
    }
  }, [
    shouldConnect,
    room
  ]);
  useEffect(() => {
    logger.log("status:", status);
  }, [
    status
  ]);
  return null;
};

export { useBroadcastSpaceEvent, useSpaceEventListener, useSpaceLiveblocksStore, useSpaceOthers, useSpaceSelf, useUpdateSpacePresence, withSpacerRoomProvider };
//# sourceMappingURL=spaces.config.js.map
